// React Frontend/src/utils/skillGraph.js
// Curated Skill Synonym Graph & Fuzzy Trigram Matcher
// Client-side high-performance deterministic keyword matching for ATS and Job Matching.

const SYNONYM_CLUSTERS = [
  // Cloud & DevOps
  ['kubernetes', 'k8s'],
  ['amazon web services', 'aws'],
  ['google cloud platform', 'gcp', 'google cloud'],
  ['microsoft azure', 'azure'],
  ['continuous integration', 'ci/cd', 'ci cd', 'continuous deployment', 'ci', 'cd'],
  ['docker', 'containerization', 'containers', 'containerized'],
  ['terraform', 'iac', 'infrastructure as code'],
  ['ansible', 'infrastructure automation'],
  ['jenkins', 'jenkins ci'],
  ['github actions', 'gh actions'],
  ['prometheus', 'grafana', 'observability', 'monitoring'],

  // Languages & Runtimes
  ['javascript', 'js', 'ecmascript'],
  ['typescript', 'ts'],
  ['node.js', 'nodejs', 'node js', 'node'],
  ['python', 'python3', 'py'],
  ['golang', 'go language', 'go'],
  ['c++', 'cpp'],
  ['c#', 'c-sharp', 'csharp', '.net', 'dotnet'],
  ['java', 'core java'],
  ['ruby', 'ruby on rails', 'rails'],
  ['rust', 'rustlang'],
  ['php', 'laravel'],
  ['swift', 'ios development', 'ios'],
  ['kotlin', 'android development', 'android'],

  // Web & Frontend Frameworks
  ['react', 'react.js', 'reactjs', 'react native'],
  ['vue', 'vue.js', 'vuejs', 'vue 3'],
  ['angular', 'angular.js', 'angularjs', 'angular 2+'],
  ['next.js', 'nextjs', 'next js'],
  ['tailwind css', 'tailwindcss', 'tailwind'],
  ['bootstrap', 'react-bootstrap'],
  ['html5', 'html', 'semantic html'],
  ['css3', 'css', 'sass', 'scss'],
  ['frontend', 'front-end', 'front end'],
  ['backend', 'back-end', 'back end'],
  ['fullstack', 'full-stack', 'full stack'],

  // Databases & Storage
  ['postgresql', 'postgres', 'psql'],
  ['mongodb', 'mongo'],
  ['mysql', 'my sql'],
  ['microsoft sql server', 'ms sql', 'mssql', 'sql server'],
  ['redis', 'in-memory cache', 'caching'],
  ['elasticsearch', 'elastic search', 'es', 'elk stack'],
  ['dynamodb', 'dynamo db', 'aws dynamodb'],
  ['relational database', 'rdbms', 'sql'],
  ['nosql', 'non-relational database'],
  ['graphql', 'graph ql', 'apollo graphql'],
  ['rest api', 'restful api', 'restful apis', 'rest', 'rest apis'],

  // Systems & Architecture
  ['microservices', 'micro-services', 'microservice architecture'],
  ['distributed systems', 'distributed computing'],
  ['system design', 'software architecture', 'system architecture'],
  ['event-driven architecture', 'event driven', 'message queues', 'kafka', 'rabbitmq', 'pub/sub'],
  ['object-oriented programming', 'oop', 'ood', 'object oriented design'],
  ['test-driven development', 'tdd', 'unit testing', 'integration testing', 'jest', 'mocha', 'cypress'],
  ['agile', 'scrum', 'kanban', 'sprint planning'],

  // AI & Data Science
  ['machine learning', 'ml'],
  ['artificial intelligence', 'ai', 'genai', 'generative ai', 'gen ai'],
  ['large language models', 'llm', 'llms', 'prompt engineering'],
  ['natural language processing', 'nlp'],
  ['deep learning', 'neural networks', 'pytorch', 'tensorflow'],
  ['data engineering', 'etl', 'data pipelines', 'spark', 'apache spark'],
  ['business intelligence', 'bi', 'power bi', 'tableau']
];

const SYNONYM_MAP = new Map();

for (const cluster of SYNONYM_CLUSTERS) {
  for (const variant of cluster) {
    const norm = variant.toLowerCase().trim();
    if (!SYNONYM_MAP.has(norm)) {
      SYNONYM_MAP.set(norm, new Set());
    }
    const set = SYNONYM_MAP.get(norm);
    for (const other of cluster) {
      set.add(other.toLowerCase().trim());
    }
  }
}

export function getSkillSynonyms(term = '') {
  const norm = term.toLowerCase().trim();
  const direct = SYNONYM_MAP.get(norm);
  if (direct) {
    return Array.from(direct);
  }
  return [norm];
}

function getTrigrams(str) {
  const s = `  ${str.toLowerCase().replace(/[^a-z0-9]/g, '')}  `;
  const trigrams = new Set();
  for (let i = 0; i < s.length - 2; i++) {
    trigrams.add(s.slice(i, i + 3));
  }
  return trigrams;
}

export function trigramSimilarity(str1, str2) {
  if (!str1 || !str2) return 0;
  if (str1.toLowerCase() === str2.toLowerCase()) return 1;

  const t1 = getTrigrams(str1);
  const t2 = getTrigrams(str2);

  if (t1.size === 0 || t2.size === 0) return 0;

  let intersection = 0;
  for (const gram of t1) {
    if (t2.has(gram)) intersection++;
  }

  return (2 * intersection) / (t1.size + t2.size);
}

export function matchesSkillInCorpus(skill, corpusText) {
  if (!skill || !corpusText) return false;

  const corpusLower = ` ${corpusText.toLowerCase().replace(/[^a-z0-9+#./-]/g, ' ')} `;
  const synonyms = getSkillSynonyms(skill);

  for (const syn of synonyms) {
    const cleanSyn = syn.toLowerCase();
    if (cleanSyn.length <= 3) {
      const regex = new RegExp(`(?:^|\\s|[.,;/()])${cleanSyn.replace(/[.+*?^${}()|[\]\\]/g, '\\$&')}(?:$|\\s|[.,;/()])`, 'i');
      if (regex.test(corpusText)) return true;
    } else {
      if (corpusLower.includes(` ${cleanSyn} `) || corpusText.toLowerCase().includes(cleanSyn)) {
        return true;
      }
    }
  }

  if (skill.length >= 5) {
    const corpusWords = corpusLower.split(/\s+/).filter(w => w.length >= 4);
    for (const word of corpusWords) {
      if (trigramSimilarity(skill, word) >= 0.85) {
        return true;
      }
    }
  }

  return false;
}
